import {
  useState,
  type MouseEvent,
  type Dispatch,
  type SetStateAction,
  useMemo,
} from 'react';
import { IFSScanResult } from '../Interfaces/IFSDirectory';
import {
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  TableCell,
  TableRow,
} from '@mui/material';
import { ILegalTransaction } from '../Interfaces/ILegalTransaction';
import { ILegalTransactionTransactionNumber } from '../Interfaces/ILegalTransactionTransactionNumber';
import { ILegalTransactionRecordNumber } from '../Interfaces/ILegalTransactionRecordNumber';
import { IFSDirectorySortedDocument } from '../Interfaces/IFSDirectorySortedDocument';
import { Divider } from 'verbum';
import { Search, VisibilityOff } from '@mui/icons-material';
import { useAlert } from '../context/AlertContext';
import { useSortFSDirectoryDocument } from '../hooks/reactQuerys/DocumentQueries';

interface IProps {
  fsScanObject: IFSScanResult;
  setFsScanResults: Dispatch<SetStateAction<IFSScanResult[]>>;
  fsDirSortedDocuments: IFSDirectorySortedDocument[];
  legalTransactions: ILegalTransaction[];
  recordNumbers: ILegalTransactionRecordNumber[];
  transactionNumbers: ILegalTransactionTransactionNumber[];
  onOpenSortingDialog: (fsScanResult: IFSScanResult) => void;
}

export const DocumentPortalScanElement = ({
  fsScanObject,
  setFsScanResults,
  fsDirSortedDocuments,
  legalTransactions,
  recordNumbers,
  transactionNumbers,
  onOpenSortingDialog,
}: IProps) => {
  const { showAlert } = useAlert();

  const { sortFSDirectoryDocument, isSortingFSDirectoryDocument } =
    useSortFSDirectoryDocument();

  const fsDirSortedDoc = fsDirSortedDocuments.find(
    fsSortedDoc =>
      fsSortedDoc.fileName === fsScanObject.fileName &&
      fsSortedDoc.idFSDirectory === fsScanObject.idFSDirectory
  );

  const assignedLegalTransaction = legalTransactions.find(
    lT =>
      lT.idLegalTransaction ===
      fsDirSortedDoc?.LegalTransactionSortedDoc_idFSDirectorySortedDoc
  );

  const currentRecordNumbers = useMemo(() => {
    if (!assignedLegalTransaction || !recordNumbers) {
      return '';
    }

    return recordNumbers
      .filter(
        recordNumber =>
          recordNumber.idLegalTransaction ===
          assignedLegalTransaction.idLegalTransaction
      )
      .map(recordNumber => recordNumber.RecordNumberEntry)
      .join(', ');
  }, [recordNumbers, assignedLegalTransaction]);

  const currentLegalTransactionNumbers = useMemo(() => {
    if (!assignedLegalTransaction || !transactionNumbers) {
      return '';
    }

    return transactionNumbers
      .filter(
        transactionNumber =>
          transactionNumber.idLegalTransaction ===
          assignedLegalTransaction.idLegalTransaction
      )
      .map(transactionNumber => transactionNumber.TransactionNumberEntry)
      .join(', ');
  }, [transactionNumbers, assignedLegalTransaction]);

  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  const handleContextMenu = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleHideDocument = async () => {
    const assignedLegalTransaction = legalTransactions.find(
      lT =>
        lT.idLegalTransaction ===
        fsDirSortedDoc?.LegalTransactionSortedDoc_idFSDirectorySortedDoc
    );

    const sortedDocument: IFSDirectorySortedDocument = {
      idFSDirectorySortedDocument: -1,
      fileName: fsScanObject.fileName,
      idFSDirectory: fsScanObject.idFSDirectory,
      LegalTransactionSortedDoc_idFSDirectorySortedDoc:
        assignedLegalTransaction?.idLegalTransaction,
      is_hidden: new Date().toISOString(),
    };

    try {
      await sortFSDirectoryDocument(sortedDocument);

      setFsScanResults(prev =>
        prev.filter(
          fsScanResult =>
            fsScanResult.idFSDirectory !== fsScanObject.idFSDirectory ||
            fsScanResult.fileName !== fsScanObject.fileName
        )
      );

      showAlert({
        text: 'Dokument ausgeblendet',
      });
    } catch {
      showAlert({
        text: 'Fehler beim Ausblenden des Dokuments',
        severity: 'error',
      });
    }
  };

  return (
    <>
      <Menu
        key={`menu-idDocumentDirecotry-${fsScanObject.idFSDirectory}-${fsScanObject.fileName}`}
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference='anchorPosition'
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuList dense>
          <MenuItem
            onClick={() => {
              onOpenSortingDialog(fsScanObject);
              handleCloseContextMenu();
            }}
          >
            <ListItemIcon>
              <Search />
            </ListItemIcon>
            <ListItemText>Öffnen</ListItemText>
          </MenuItem>
          <Divider />
          {!fsScanObject.isSorted && (
            <MenuItem
              onClick={() => {
                handleHideDocument();
                handleCloseContextMenu();
              }}
            >
              <ListItemIcon>
                <VisibilityOff />
              </ListItemIcon>
              <ListItemText>Ausblenden</ListItemText>
              {isSortingFSDirectoryDocument && (
                <CircularProgress size={20} color='inherit' sx={{ ml: 1 }} />
              )}
            </MenuItem>
          )}
        </MenuList>
      </Menu>

      <TableRow
        sx={{
          cursor: 'pointer',
          backgroundColor: fsScanObject.isSorted
            ? 'rgb(237, 247, 237)'
            : undefined, // Use the 'success' color
          color: fsScanObject.isSorted ? 'rgb(30, 70, 32)' : undefined, // Adjust text color for readability
        }}
        onClick={() => onOpenSortingDialog(fsScanObject)}
        onContextMenu={handleContextMenu}
      >
        <TableCell>{fsScanObject.fileName}</TableCell>
        <TableCell>
          {fsScanObject.isSorted ? <>Sortiert</> : <>Unsortiert</>}
        </TableCell>
        <TableCell>{currentRecordNumbers}</TableCell>
        <TableCell>{currentLegalTransactionNumbers}</TableCell>
        <TableCell>{assignedLegalTransaction?.Title}</TableCell>
      </TableRow>
    </>
  );
};
